import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BrainIcon, TimerIcon, AnchorIcon } from 'lucide-react';
export function MechanismSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-[#FFFAF5] px-4 py-16 md:py-24" data-id="element-85">
      <div className="max-w-4xl mx-auto" data-id="element-86">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-87">
          <span className="inline-block px-3 py-1 text-sm font-semibold text-[#7B9E6B] bg-[#E8F0E4] rounded-full mb-4" data-id="element-88">
            Neurociencia aplicada
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#3D2B1F] leading-tight mb-4" data-id="element-89">
            El Mecanismo Único: La Pausa de 4 Segundos
          </h2>
          <p className="text-lg text-[#5C4A3A] max-w-2xl mx-auto" data-id="element-90">
            Esto <strong className="text-[#3D2B1F]" data-id="element-91">no es</strong> un curso de
            psicología de 10 horas. Es un{' '}
            <span className="text-[#C75B2A] font-bold" data-id="element-92">
              protocolo de emergencia
            </span>{' '}
            basado en neurociencia aplicada.
          </p>
        </motion.div>

        {/* Visual: The 4 */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={isInView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.7,
        delay: 0.2,
        type: 'spring',
        stiffness: 100
      }} className="flex justify-center mb-12" data-id="element-93">
          <div className="relative w-36 h-36 md:w-44 md:h-44 flex items-center justify-center" data-id="element-94">
            {/* Outer ring */}
            <div className="absolute inset-0 rounded-full border-4 border-[#7B9E6B]/30" data-id="element-95" />
            {/* Inner ring animated */}
            <motion.div className="absolute inset-2 rounded-full border-[3px] border-dashed border-[#7B9E6B]/50" animate={{
            rotate: 360
          }} transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-96" />
            {/* Number */}
            <span className="relative text-7xl md:text-8xl font-bold text-[#7B9E6B]" style={{
            fontFamily: "'DM Serif Display', Georgia, serif"
          }} data-id="element-97">
              4
            </span>
            <span className="absolute bottom-4 text-sm font-semibold text-[#7B9E6B] tracking-wide" data-id="element-98">
              SEGUNDOS
            </span>
          </div>
        </motion.div>

        {/* Three pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-99">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-white rounded-xl p-6 shadow-sm shadow-[#3D2B1F]/5 border border-[#7B9E6B]/10" data-id="element-100">
            <div className="w-10 h-10 rounded-lg bg-[#E8F0E4] flex items-center justify-center mb-4" data-id="element-101">
              <BrainIcon className="w-5 h-5 text-[#7B9E6B]" aria-hidden="true" data-id="element-102" />
            </div>
            <h3 className="text-lg font-bold text-[#3D2B1F] mb-2" style={{
            fontFamily: "'DM Serif Display', Georgia, serif"
          }} data-id="element-103">
              Identifica el secuestro
            </h3>
            <p className="text-[#5C4A3A] text-base leading-relaxed" data-id="element-104">
              Aprende a reconocer el "secuestro de la amígdala" — el momento
              exacto en que tu cerebro pierde el control racional.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.45
        }} className="bg-white rounded-xl p-6 shadow-sm shadow-[#3D2B1F]/5 border border-[#7B9E6B]/10" data-id="element-105">
            <div className="w-10 h-10 rounded-lg bg-[#E8F0E4] flex items-center justify-center mb-4" data-id="element-106">
              <AnchorIcon className="w-5 h-5 text-[#7B9E6B]" aria-hidden="true" data-id="element-107" />
            </div>
            <h3 className="text-lg font-bold text-[#3D2B1F] mb-2" style={{
            fontFamily: "'DM Serif Display', Georgia, serif"
          }} data-id="element-108">
              Usa el ancla física
            </h3>
            <p className="text-[#5C4A3A] text-base leading-relaxed" data-id="element-109">
              Un gesto corporal simple que detiene tu impulso de gritar en menos
              de 4 segundos. Es biología, no fuerza de voluntad.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.6
        }} className="bg-white rounded-xl p-6 shadow-sm shadow-[#3D2B1F]/5 border border-[#7B9E6B]/10" data-id="element-110">
            <div className="w-10 h-10 rounded-lg bg-[#E8F0E4] flex items-center justify-center mb-4" data-id="element-111">
              <TimerIcon className="w-5 h-5 text-[#7B9E6B]" aria-hidden="true" data-id="element-112" />
            </div>
            <h3 className="text-lg font-bold text-[#3D2B1F] mb-2" style={{
            fontFamily: "'DM Serif Display', Georgia, serif"
          }} data-id="element-113">
              Actúa con firmeza y calma
            </h3>
            <p className="text-[#5C4A3A] text-base leading-relaxed" data-id="element-114">
              Recupera el control y responde con autoridad real — sin gritos,
              sin culpa, sin arrepentimientos.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}